// src/components/family/FamilySection.jsx

import { useEffect, useState, useCallback } from 'react';
import { doc, getDoc, getDocs, deleteDoc, updateDoc, deleteField, collection } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase-client';
import CreateFamilyForm from '../forms/createFamily';
import AddOrInviteFamilyMemberForm from '../forms/addOrInviteFamilyMember';
import CreateChildAccountForm from '../forms/createChildAccount';
import FamilyCard from './familyComponents/FamilyCard';
import DestructiveActionDialog from '../ui/DestructiveActionDialog';
export default function FamilySection({
  user,
  onChildCreated
}) {
  const [families, setFamilies] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [addingFamilyId, setAddingFamilyId] = useState(null);
  const [addMode, setAddMode] = useState('invite');
  const [addSuccessFamilyId, setAddSuccessFamilyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingFamilyDelete, setPendingFamilyDelete] = useState(null);
  const [deletingFamily, setDeletingFamily] = useState(false);
  const fetchFamilies = useCallback(async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const roles = userDoc.data()?.family_roles || {};
      const familyIds = Object.keys(roles);
      const docs = await Promise.all(familyIds.map(async id => {
        const familyDoc = await getDoc(doc(db, 'families', id));
        if (!familyDoc.exists()) return null;
        const membersSnap = await getDocs(collection(db, 'families', id, 'members'));
        const members = membersSnap.docs.map(doc => ({
          userId: doc.id,
          ...doc.data()
        }));
        return {
          id,
          ...familyDoc.data(),
          members,
          role: roles[id]
        };
      }));
      setFamilies(docs.filter(Boolean).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {} finally {
      setLoading(false);
    }
  }, [user.uid]);
  useEffect(() => {
    fetchFamilies();
  }, [fetchFamilies]);
  const handleDeleteFamily = family => {
    setPendingFamilyDelete(family);
  };
  const confirmDeleteFamily = async () => {
    if (!pendingFamilyDelete) return;
    setDeletingFamily(true);
    try {
      const deleteFamilyFn = httpsCallable(functions, 'deleteFamily');
      await deleteFamilyFn({
        familyId: pendingFamilyDelete.id
      });
      setPendingFamilyDelete(null);
      fetchFamilies();
    } catch (err) {
      alert('Failed to delete family.');
    } finally {
      setDeletingFamily(false);
    }
  };
  const handleLeaveFamily = async family => {
    if (!window.confirm(`Are you sure you want to leave the family "${family.name}"?`)) return;
    const memberRole = family.role;
    const otherParents = family.members.filter(m => m.role === 'parent' && m.userId !== user.uid);
    if (memberRole === 'parent' && otherParents.length === 0) {
      alert('You are the only parent in this family. Another parent must remain before you can leave.');
      return;
    }
    try {
      await deleteDoc(doc(db, 'families', family.id, 'members', user.uid));
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        [`family_roles.${family.id}`]: deleteField()
      });
      fetchFamilies();
    } catch (err) {
      alert('Failed to leave family.');
    }
  };
  if (loading) return <p>Loading family info...</p>;
  return <>
      <h2 className="section-title">Your Families</h2>

      {families.length > 0 ? families.map(family => <div key={family.id} className="section">
            <h3>
              {family.name} ({family.role})
            </h3>

                <div className="cards">
              {family.members.map(member => <FamilyCard key={member.userId} member={member} currentUser={user.uid} currentUserRole={family.role} familyId={family.id} onAction={fetchFamilies} />)}
            </div>

            {family.role === 'parent' ? <div style={{
        marginTop: '2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
      }}>
                {addSuccessFamilyId === family.id && <p style={{
          color: 'green'
        }}>Member added!</p>}

                {addingFamilyId === family.id ? <>
                    <div style={{
            display: 'flex',
            gap: '0.5rem'
          }}>
                      <button className={`button ${addMode === 'invite' ? '' : 'button-secondary'}`} type="button" onClick={() => setAddMode('invite')}>
                        Invite Existing User
                      </button>
                      <button className={`button ${addMode === 'create-child' ? '' : 'button-secondary'}`} type="button" onClick={() => setAddMode('create-child')}>
                        Create Child Account
                      </button>
                    </div>

                    {addMode === 'invite' ? <AddOrInviteFamilyMemberForm family={family} onAdded={() => {
            setAddSuccessFamilyId(family.id);
            setAddingFamilyId(null);
            fetchFamilies();
            setTimeout(() => setAddSuccessFamilyId(null), 3000);
          }} onCancel={() => setAddingFamilyId(null)} /> : <CreateChildAccountForm family={family} onCreated={(child) => {
            setAddSuccessFamilyId(family.id);
            setAddingFamilyId(null);
            fetchFamilies();
            setTimeout(() => setAddSuccessFamilyId(null), 3000);
            onChildCreated?.(child);
          }} onCancel={() => setAddingFamilyId(null)} />}
                  </> : <button className="button" onClick={() => {
          setAddMode('invite');
          setAddingFamilyId(family.id);
        }}>
                    Add Family Member
                  </button>}

                <button className="button button-danger" onClick={() => handleDeleteFamily(family)}>
                  Delete Family
                </button>

                <button className="button button-secondary" onClick={() => handleLeaveFamily(family)}>
                  Leave Family
                </button>
              </div> : <div style={{
        marginTop: '1rem'
      }}>
                <button className="button button-danger" onClick={() => handleLeaveFamily(family)}>
                  Leave Family
                </button>
              </div>}
          </div>) : <p>You are not currently part of any families.</p>}

      <div style={{
      marginTop: '3rem'
    }}>
        <h3>Create a New Family</h3>
        {!families.length || showCreateForm ? <CreateFamilyForm user={user} onCreated={fetchFamilies} onCancel={() => setShowCreateForm(false)} /> : <button className="button" onClick={() => setShowCreateForm(true)}>
            Create New Family
          </button>}
      </div>
      <DestructiveActionDialog
        isOpen={Boolean(pendingFamilyDelete)}
        title="Permanently delete this family?"
        message={`Deleting “${pendingFamilyDelete?.name || 'this family'}” removes the family group and its relationships. This cannot be undone.`}
        confirmationText={pendingFamilyDelete?.name || ''}
        confirmLabel="Delete family"
        cancelLabel="Keep family"
        busy={deletingFamily}
        onClose={() => !deletingFamily && setPendingFamilyDelete(null)}
        onConfirm={confirmDeleteFamily}
      />
    </>;
}
